import React from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import theme from '@src/styles/theme';
import actions from '@src/redux/actions';
import { getPackageName } from '@src/services/package';
import WysiwygRoundedIcon from '@mui/icons-material/WysiwygRounded';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import CalenderRounded from '@src/assets/icons/calander-icon.svg';
import LockIconRounded from '@src/assets/icons/lock-icon.svg';
import ROUTES from '@src/constants/route';
import { StyledPopupTransferPackage } from './index.style';

const ContentTransferPackage = ({ setHasAgreeToMigrateToV3 }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { usingPackage } = useSelector((state) => state.user || {});
  const { user } = useSelector((state) => state.auth);
  const { packageExpiryDate, preservationExpiryDate } = user;
  const { getFeatureValue } = useFeatureFlags();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const migrateOldPkgToV3 = getFeatureValue(
    FEATURE_KEYS.MIGRATE_OLD_PKG_TO_V3,
    { userId: user.id },
  );

  const oldPackageName = () => {
    const name = getPackageName(
      usingPackage.name,
      usingPackage.code,
      language,
      t,
      true,
      false,
    );

    return name?.replace('-', '');
  };

  const newPackageName = () => {
    const name = getPackageName(
      '',
      user?.studioUsageOptions?.code,
      language,
      t,
      true,
      migrateOldPkgToV3,
    );
    return name?.replace('-', '');
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const timeExpiredDate =
    moment(packageExpiryDate).format('DD/MM/YYYY - HH:mm');

  const timePreservationExpiryDate = moment(preservationExpiryDate).format(
    'DD/MM/YYYY - HH:mm',
  );

  const isExpiredPackage = checkExpiredDate(packageExpiryDate);

  const handleRedirectToPayment = () => {
    dispatch(
      actions.auth.updateUserInfo({ ...user, hasAgreeToMigrateToV3: true }),
    );
    setHasAgreeToMigrateToV3(true);
    history.push(ROUTES.PAYMENT);
  };

  const handleClickAgreeToMigrate = () => {
    setHasAgreeToMigrateToV3(true);
    localStorage.setItem(`hasAgreeToMigrateToV3-${user?.id}`, true);
  };

  return (
    <StyledPopupTransferPackage>
      <div className="description-transfer-package">
        {t('descriptionTransferPackage')}
      </div>
      <div className="info-transfer-package">
        <div className="old-package">
          <div className="type-package">{t('oldPackage')}</div>
          <div className="name-package">{oldPackageName()}</div>
        </div>
        <ArrowForwardRoundedIcon className="icon-arrow-right" />
        <div className="new-package">
          <div className="type-package">{t('newPackage')}</div>
          <div className="name-package">{newPackageName()}</div>
        </div>
      </div>

      <div className="info-change">
        <div className="item">
          <div className="icon-item">
            <WysiwygRoundedIcon className="icon-wysiwy" />
          </div>
          <div className="info-package" component="span">
            <span>{t('remainingByCharacters')} </span>
            {!isMobile && ':'}{' '}
            <strong>
              {`${
                isExpiredPackage
                  ? user?.lockCharacters?.toLocaleString(language)
                  : user?.remainingCharacters?.toLocaleString(language)
              } ${t('charactersLower')}`}
            </strong>
          </div>
        </div>
        <div className="item">
          <div className="icon-item">
            <img src={CalenderRounded} alt="CalenderRounded" />
          </div>
          <div className="info-package">
            <span>{t('expiryDate')} </span>
            {!isMobile && ':'}
            <span>
              <strong>{timeExpiredDate}</strong>{' '}
              {isExpiredPackage && (
                <span style={{ color: 'black' }}> {t('expiredDateText')}</span>
              )}
            </span>
          </div>
        </div>
        {isExpiredPackage && preservationExpiryDate && (
          <div className="item">
            <div className="icon-item">
              <img src={LockIconRounded} alt="LockIconRounded" />
            </div>
            <div className="info-package">
              <span> {t('preservationExpiryDate')}</span> {!isMobile && ': '}
              <strong>{timePreservationExpiryDate}</strong>
            </div>
          </div>
        )}
      </div>

      <div className="notes">
        <div className="note-title">{`${t('noteV2')}:`}</div>
        <div className="note-list">
          <div>
            {t('theAutoRenewalFeatureWillBeTemporarilySuspendedForOldPackage')}
          </div>
          <div>
            {t('theCharacterRetentionPolicyStaysTheSameUntilYouGetANewPlan')}
          </div>
          <div>
            {t('exploreTheBenefitsOfOtherPlansHere')}{' '}
            <span
              className="right-here-text"
              role="button"
              tabIndex="0"
              onClick={handleRedirectToPayment}
            >
              {t('here')}
            </span>
            <span>.</span>
          </div>
        </div>
      </div>

      <div className="transfer-button">
        <button
          className="button"
          type="button"
          onClick={handleClickAgreeToMigrate}
        >
          {t('gotItV2')}
        </button>
      </div>
    </StyledPopupTransferPackage>
  );
};

export default ContentTransferPackage;
